import React, { useState, useEffect } from 'react';
import { Settings, Moon, Sun, Monitor, User, Bell, Shield, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const { user } = useAuth();
  const { darkMode, toggleDarkMode, resetToSystemPreference } = useTheme();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    updates: true
  });

  const handleDarkModeToggle = () => {
    toggleDarkMode();
    toast.success(!darkMode ? 'Modo escuro ativado' : 'Modo claro ativado');
  };

  const handleResetToSystem = () => {
    resetToSystemPreference();
    toast.success('Preferência resetada para o sistema');
  };

  const handleNotificationChange = (type) => {
    setNotifications(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    toast.success('Configuração de notificação atualizada');
  };

  const SettingCard = ({ icon: Icon, title, description, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {description}
          </p>
          {children}
        </div>
      </div>
    </div>
  );

  const Toggle = ({ enabled, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </span>
      <button
        type="button"
        className={`${
          enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
        onClick={onChange}
      >
        <span
          className={`${
            enabled ? 'translate-x-5' : 'translate-x-0'
          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Settings className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Configurações
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Personalize sua experiência e gerencie suas preferências
          </p>
        </div>

        {/* Settings Grid */}
        <div className="space-y-6">
          {/* Aparência */}
          <SettingCard
            icon={Palette}
            title="Aparência"
            description="Personalize a aparência da interface"
          >
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {darkMode ? (
                      <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <Sun className="w-5 h-5 text-yellow-500" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {darkMode ? 'Modo Escuro' : 'Modo Claro'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {darkMode ? 'Interface escura para reduzir o cansaço visual' : 'Interface clara e brilhante'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDarkModeToggle}
                    className={`${
                      darkMode ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        darkMode ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Monitor className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Seguir Sistema
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Usar a preferência de tema do sistema operacional
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleResetToSystem}
                    className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Resetar
                  </button>
                </div>
              </div>
            </div>
          </SettingCard>

          {/* Notificações */}
          <SettingCard
            icon={Bell}
            title="Notificações"
            description="Configure como você deseja receber notificações"
          >
            <div className="space-y-4">
              <Toggle
                enabled={notifications.email}
                onChange={() => handleNotificationChange('email')}
                label="Notificações por email"
              />
              <Toggle
                enabled={notifications.push}
                onChange={() => handleNotificationChange('push')}
                label="Notificações push"
              />
              <Toggle
                enabled={notifications.updates}
                onChange={() => handleNotificationChange('updates')}
                label="Atualizações do sistema"
              />
            </div>
          </SettingCard>

          {/* Conta */}
          <SettingCard
            icon={User}
            title="Informações da Conta"
            description="Gerencie suas informações pessoais"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nome completo
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {user?.name || 'Não informado'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {user?.email || 'Não informado'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tipo de usuário
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.role === 'admin' 
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }`}>
                  {user?.role === 'admin' ? 'Administrador' : 'Usuário'}
                </span>
              </div>
            </div>
          </SettingCard>

          {/* Segurança */}
          <SettingCard
            icon={Shield}
            title="Segurança"
            description="Configurações de segurança da sua conta"
          >
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/change-password')}
                className="w-full text-left px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Alterar senha
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Atualize sua senha para manter sua conta segura
                    </p>
                  </div>
                  <div className="text-gray-400 dark:text-gray-500">
                    →
                  </div>
                </div>
              </button>
            </div>
          </SettingCard>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sistema de Análise de Seguros - Versão 1.0.0
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Sistema de Análise de Seguros
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;